import { Box } from '@mui/material';
import { useDrop } from 'react-dnd';
import type { DropTargetMonitor } from 'react-dnd';
import { useState, useEffect, useMemo, useRef } from 'react';
import ShortUniqueId from 'short-unique-id';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { TAlert } from '../../../types/types';
import EmailCard from './EmailCard'; // Adjust import path
import { useDispatch } from 'react-redux';
import {
  updateRowSecurityFilters,
  removeRowSecurityCard,
  updateUserSelection,
  callQueryAPI,
} from '../../../redux/ChartPoperties/ChartControlsActions';

interface DragItem {
  type: 'card' | 'calculation';
  boxUUID?: string;
  bIndex?: number;
  fieldData?: {
    dataType?: string;
    fieldname?: string;
    displayname?: string;
    tableId?: string;
  };
}

interface CollectedProps {
  isOver: boolean;
  canDrop: boolean;
}

interface IDropZone {
  propKey: string;
  chartControls: any;
}

interface EmailCardData {
  id: string;
  name: string;
}

const EmailDropZone = ({ propKey, chartControls }: IDropZone) => {
  const [alert, setAlert] = useState<null | TAlert>(null);
  const currentOperator =
    chartControls?.properties[propKey]?.rowSecurity?.currentOperator?.toLowerCase();
  const [cards, setCards] = useState<EmailCardData[]>([]);

  const uid = new ShortUniqueId({ length: 4 });
  const dispatch = useDispatch();
  const uniqueId = uid();

  const isFirstRender = useRef(true);

  const userSelection = useMemo(() => {
    return (
      chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[currentOperator]
        ?.filters?.[0]?.userSelection || []
    );
  }, [
    chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[currentOperator]
      ?.filters?.[0]?.userSelection,
  ]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dispatch(callQueryAPI(propKey, true));
  }, [
    chartControls.properties[propKey]?.rowSecurity?.filterPanels[currentOperator]?.filters,
    chartControls.properties[propKey]?.rowSecurity?.filterPanels[currentOperator],
  ]);

  const [{ isOver, canDrop }, drop] = useDrop<DragItem, void, CollectedProps>({
    accept: ['card', 'calculation'],
    canDrop: (item) => item.bIndex === 99,
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    drop: (item: DragItem) => {
      const handleDrop = async () => {
        const dataType = item.fieldData?.dataType?.toLowerCase();
        const fieldName = item.fieldData?.fieldname?.toLowerCase();
        const displayName = item.fieldData?.displayname?.toLowerCase();

        const isEmailLike =
          dataType === 'email' || fieldName?.includes('email') || displayName?.includes('email');

        const isCalculationValid =
          item.type === 'calculation' && ['email', 'string', 'text'].includes(dataType || '');

        if (item.type === 'card' && !isEmailLike) {
          setAlert({
            message: 'Only fields related to emails can be dropped here!',
            severity: 'error',
          });
          return;
        }

        if (item.type === 'calculation' && !isCalculationValid) {
          setAlert({
            message: 'Only flows related to emails can be dropped here!',
            severity: 'error',
          });
          return;
        }

        const filterPanels =
          chartControls?.properties?.[propKey]?.rowSecurity?.filterPanels?.[currentOperator];
        const existingFilters = filterPanels?.filters || [];

        const isDuplicate = existingFilters.some(
          (f: any) => f.fieldName === item.fieldData?.fieldname,
        );

        if (isDuplicate) return;

        const filteredData = {
          dataType: item.fieldData?.dataType,
          displayName: item.fieldData?.displayname,
          fieldName: item.fieldData?.fieldname,
          source: uniqueId || '',
          sourceType: item.type || '',
          tableId: item.fieldData?.tableId,
          userSelection: userSelection,
          calculationUUID: item.boxUUID,
          filterType: `row_level_security_${currentOperator?.toLowerCase()}`,
          operator: 'in',
          shouldExclude: false,
        };

        dispatch(updateRowSecurityFilters(propKey, filteredData, currentOperator));
        const newCard: EmailCardData = {
          id: uniqueId,
          name: item.fieldData?.displayname || '',
        };

        setCards([newCard]);
      };
      handleDrop();
    },
  });

  const handleDelete = (id: string) => {
    dispatch(updateUserSelection(propKey, currentOperator?.toLowerCase(), []));
    dispatch(removeRowSecurityCard(propKey, id, currentOperator));
    setCards((prevCards) => prevCards.filter((card) => card.id !== id));
    // fetchAllData();
    dispatch(callQueryAPI(propKey, true));
  };

  useEffect(() => {
    const filtersFromRedux =
      chartControls?.properties?.[propKey]?.rowSecurity?.filterPanels?.[currentOperator]?.filters ||
      [];

    const cardsFromRedux = filtersFromRedux.map((filter: any) => ({
      id: filter.source,
      name: filter.displayName,
    }));

    setCards(cardsFromRedux);
  }, [chartControls?.properties?.[propKey]?.rowSecurity, propKey, currentOperator]);

  //----------------commenting this as we found it unnecessary, if we found any need of this then we will use. Also, SortChartData in fetchData is causing issue if we have showhide filter. If we have showHide filter then SortChartData in fetch data is not respecting showHide filter. So if we have to use this then we have to fix for SortChartData in fetchData so that security setting respects showHide filters.-----------------------------//
  // useEffect(() => {
  //   fetchData();
  // }, [
  //   chartProperties.properties[propKey].chartAxes,
  //   chartProperties.properties[propKey].chartType,
  //   chartProperties.properties[propKey].axesEdited,
  //   chartControls.properties[propKey]?.rowSecurity?.filterPanels[currentOperator]?.filters,
  //   chartControls.properties[propKey]?.rowSecurity?.filterPanels[currentOperator],
  // ]);

  return (
    <Box
      ref={drop}
      sx={{
        // borderRadius: '8px',
        padding: 1,
        minHeight: '60px',
        border: isOver && canDrop ? '1px dashed #2bb9bb' : 'none',
        backgroundColor: isOver && canDrop ? 'rgba(43, 185, 187, 0.05)' : 'inherit',
      }}
    >
      {alert && (
        <NotificationDialog
          onCloseAlert={() => setAlert(null)}
          severity={alert.severity}
          testMessage={alert?.message}
          openAlert={!!alert}
        />
      )}

      {cards.length > 0 ? (
        cards.map((card) => (
          <EmailCard
            key={card.id}
            id={card.id}
            name={card.name}
            handleDelete={() => handleDelete(card.id)}
            chartControls={chartControls}
            propKey={propKey}
          />
        ))
      ) : (
        <span>Drag and Drop an email field or calculation</span>
      )}
    </Box>
  );
};

export default EmailDropZone;
